import { useMemo, type ComponentProps } from 'react';

import MenuItem from '@liquidweb-dev/undercurrent/MenuItem';
import TextField from '@liquidweb-dev/undercurrent/TextField';
import { allCountries } from 'country-region-data';
import { getRegionLabel } from 'utility/addressLabel';

export type RegionSelectProps = Omit<
	ComponentProps<typeof TextField>,
	'select' | 'children'
> & {
	country: string;
	/** set to true to have option values be the country-region-data name of the region instead of the region code */
	preferLongName?: boolean;
};

const omitSelectCountries = ['GB'];

export function RegionSelect({
	country,
	value,
	disabled,
	label = 'State',
	preferLongName,
	...rest
}: RegionSelectProps) {
	/** Region list based on country */
	const regionList = useMemo(() => {
		return (
			allCountries
				?.find(([, slug]) => country === slug)?.[2]
				// exclude Micronesia from US list
				.filter(([, slug]) => (country === 'US' ? slug !== 'FM' : true)) || []
		);
	}, [country]);

	/** Custom region label (state/province) */
	const regionLabel = useMemo(() => {
		return getRegionLabel(country);
	}, [country]);

	const useDropDown = useMemo(
		() => !omitSelectCountries.includes(country),
		[country],
	);

	const isDisabled = disabled || !(regionList?.length > 0);

	return (
		<TextField
			value={value}
			select={useDropDown}
			label={regionLabel || label}
			autoComplete="region"
			disabled={isDisabled}
			{...rest}
		>
			{regionList.map(([name, shortName]) => (
				<MenuItem key={shortName} value={preferLongName ? name : shortName}>
					{name}
				</MenuItem>
			))}
		</TextField>
	);
}

export default RegionSelect;
